import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Crown,
  Play,
  Lock,
  CalendarCheck,
  CheckCircle2,
  ShieldCheck,
  Quote,
} from "lucide-react";
import { GoldParticles } from "../components/effects/GoldParticles";
import { MaskLine, maskLineParent } from "../components/effects/MaskLine";
import { CALENDLY_URL, LOGO_URL, TESTIMONIAL_VIDEOS } from "../data/content";

// A single testimonial video: no fast-forward, must reach the end.
const TestimonialVideo = ({ index, src, role, done, onComplete }) => {
  const ref = useRef(null);
  const maxRef = useRef(0);
  const [playing, setPlaying] = useState(false);

  const onTimeUpdate = () => {
    const v = ref.current;
    if (!v) return;
    if (v.currentTime > maxRef.current) maxRef.current = v.currentTime;
  };
  const onSeeking = () => {
    const v = ref.current;
    if (!v) return;
    if (v.currentTime > maxRef.current + 0.4) v.currentTime = maxRef.current;
  };

  const start = () => {
    const v = ref.current;
    if (v) {
      v.play();
      setPlaying(true);
    }
  };

  return (
    <motion.figure
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={`glass group relative overflow-hidden rounded-2xl transition-shadow ${
        done ? "ring-2 ring-green-500/50" : ""
      }`}
      data-testid={`testimonial-video-${index}`}
    >
      {done && (
        <span
          className="absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-full bg-green-500 px-2.5 py-1 font-body text-[10px] font-bold uppercase tracking-wider text-[#0B0E17] shadow-lg"
          data-testid={`testimonial-done-${index}`}
        >
          <CheckCircle2 className="h-3 w-3" /> Vu
        </span>
      )}
      <div className="relative aspect-[9/16] w-full overflow-hidden bg-black">
        <video
          ref={ref}
          src={src}
          className="h-full w-full object-cover"
          controls={playing}
          playsInline
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={onTimeUpdate}
          onSeeking={onSeeking}
          onEnded={() => {
            setPlaying(false);
            onComplete(index);
          }}
        />
        {!playing && !done && (
          <button
            onClick={start}
            aria-label="Lire le témoignage"
            data-testid={`testimonial-play-${index}`}
            className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/20"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#D4AF37] text-[#0B0E17] shadow-2xl transition-transform group-hover:scale-110">
              <Play className="ml-0.5 h-6 w-6 fill-current" />
            </span>
          </button>
        )}
      </div>
      <figcaption className="p-4 text-center">
        <p className="font-heading text-lg leading-tight text-[#FDFBF7]">{role}</p>
      </figcaption>
    </motion.figure>
  );
};

export default function Reservation() {
  const [completed, setCompleted] = useState(() => new Set());

  const markComplete = (i) =>
    setCompleted((prev) => {
      const next = new Set(prev);
      next.add(i);
      return next;
    });

  const total = TESTIMONIAL_VIDEOS.length;
  const watched = completed.size;
  const allWatched = watched >= total;
  const pct = total ? (watched / total) * 100 : 0;

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0B0E17] text-[#FDFBF7]">
      <div className="light-rays -z-10 opacity-40" />
      <div className="absolute inset-0 -z-10 radial-glow" />
      <GoldParticles count={16} />

      <div className="mx-auto max-w-5xl px-5 py-12 md:py-16">
        {/* Kinetic hero with masked line-by-line reveal */}
        <motion.div
          variants={maskLineParent}
          initial="hidden"
          animate="show"
          className="text-center"
        >
          <motion.img
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            src={LOGO_URL}
            alt="Logo"
            className="mx-auto h-20 w-20 object-contain drop-shadow-[0_0_25px_rgba(212,175,55,0.35)]"
          />
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#1A2033]/40 px-4 py-1.5"
          >
            <Crown className="h-4 w-4 text-[#D4AF37]" />
            <span className="font-body text-xs uppercase tracking-[0.24em] text-[#C9A04E]">
              Dernière étape · Candidature éligible
            </span>
          </motion.div>

          <h1 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
            <MaskLine>Écoutez celles et ceux</MaskLine>
            <MaskLine className="text-gold-gradient shimmer font-medium">
              qui ont osé se lancer
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="mx-auto mt-6 max-w-xl font-body text-sm text-[#A1A9C3] md:text-base"
          >
            Regardez <strong className="text-[#FDFBF7]">chaque témoignage en entier</strong> : le
            bouton de réservation se débloquera une fois les {total} vidéos visionnées.
          </motion.p>
        </motion.div>

        {/* Watch progress */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.6 }}
          className="mx-auto mt-9 max-w-md"
        >
          <div className="flex items-center justify-between font-body text-xs text-[#A1A9C3]">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37]" /> Avance rapide désactivée
            </span>
            <span data-testid="watch-progress-count">
              {watched} / {total} témoignages
            </span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#1A2033]">
            <div
              className="h-full rounded-full bg-[#D4AF37] transition-[width] duration-500"
              style={{ width: `${pct}%` }}
              data-testid="watch-progress-bar"
            />
          </div>
        </motion.div>

        {/* Testimonial videos grid */}
        <section className="mt-12" data-testid="reservation-testimonials">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-5">
            {TESTIMONIAL_VIDEOS.map((t, i) => (
              <TestimonialVideo
                key={i}
                index={i}
                src={t.src}
                role={t.role}
                done={completed.has(i)}
                onComplete={markComplete}
              />
            ))}
          </div>
        </section>

        {/* CTA — locked until all testimonials watched */}
        <div className="mt-14 text-center">
          {allWatched ? (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-green-500/40 bg-green-500/10 px-4 py-1.5">
                <CheckCircle2 className="h-4 w-4 text-green-400" />
                <span className="font-body text-sm text-green-300">
                  Tous les témoignages ont été visionnés — réservation débloquée
                </span>
              </div>
              <div>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reservation-calendly-button"
                  className="cta-glow inline-flex items-center justify-center gap-3 rounded-full bg-[#D4AF37] px-9 py-4 font-body text-lg font-semibold text-[#0B0E17] transition-all hover:-translate-y-0.5 hover:bg-[#e6c451]"
                >
                  <CalendarCheck className="h-5 w-5" /> Choisir mon créneau de rendez-vous
                </a>
              </div>
            </motion.div>
          ) : (
            <>
              <button
                disabled
                data-testid="reservation-calendly-locked"
                className="inline-flex cursor-not-allowed items-center justify-center gap-3 rounded-full border border-[#D4AF37]/20 bg-[#1A2033]/60 px-9 py-4 font-body text-lg font-semibold text-[#A1A9C3]"
              >
                <Lock className="h-5 w-5" /> Réservation verrouillée
              </button>
              <p className="mt-4 font-body text-xs uppercase tracking-[0.18em] text-[#A1A9C3]">
                Encore {total - watched} témoignage{total - watched > 1 ? "s" : ""} à regarder
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
